import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CloudServerOutlined,
  CodeOutlined,
  GlobalOutlined,
  NodeIndexOutlined,
  DatabaseOutlined,
  SafetyOutlined,
  GithubOutlined,
  ArrowRightOutlined,
  ThunderboltOutlined,
  ApiOutlined,
  RocketOutlined,
} from '@ant-design/icons';
import { useAuthStore } from '../store/authStore';
import './Home.css';

const FEATURES = [
  {
    icon: <CloudServerOutlined />,
    title: 'MQTT 设备接入',
    desc: '边缘设备通过 mqtt_frpc 插件长连接上报 sysinfo / netinfo / pppoe_status，后端串行化写入 MySQL，主键冲突零风险。',
    tag: 'Realtime',
    color: 'cyan',
  },
  {
    icon: <CodeOutlined />,
    title: 'Web SSH 终端',
    desc: 'xterm.js + WebSocket + FRP 反向隧道，票据签发 + JWT 鉴权，浏览器一键登录设备 root shell。',
    tag: 'WebSocket',
    color: 'purple',
  },
  {
    icon: <GlobalOutlined />,
    title: 'PPPoE 多实例拨号',
    desc: 'ppp_go 插件支持 macvlan + pppd + 策略路由，单机多拨号实例并发，MAC 首次生成后持久化到 JSON。',
    tag: 'Edge',
    color: 'pink',
  },
  {
    icon: <NodeIndexOutlined />,
    title: '静态 IP 与路由',
    desc: 'VLAN 接口配置、iproute2 路由表注册、IPv4/IPv6 双栈、DNS 下发，全部通过 REST API 远程编排。',
    tag: 'Network',
    color: 'blue',
  },
  {
    icon: <DatabaseOutlined />,
    title: 'API 全量日志',
    desc: '全局拦截器记录每一次接口调用的请求头、参数、响应码、耗时，配合定时任务按保留天数清理。',
    tag: 'Observability',
    color: 'green',
  },
  {
    icon: <SafetyOutlined />,
    title: 'JWT 鉴权与限流',
    desc: 'Passport Local + JWT 双策略，全局 ThrottlerGuard 三级窗口（1s/10s/1min）防御暴力调用。',
    tag: 'Security',
    color: 'orange',
  },
];

const STACK = [
  { name: 'NestJS 10', kind: 'backend' },
  { name: 'TypeORM', kind: 'backend' },
  { name: 'MySQL', kind: 'backend' },
  { name: 'MQTT.js', kind: 'backend' },
  { name: 'Passport JWT', kind: 'backend' },
  { name: 'Swagger', kind: 'backend' },
  { name: 'ssh2', kind: 'backend' },
  { name: 'React 18', kind: 'frontend' },
  { name: 'TypeScript', kind: 'frontend' },
  { name: 'Ant Design 5', kind: 'frontend' },
  { name: 'Zustand', kind: 'frontend' },
  { name: 'xterm.js', kind: 'frontend' },
  { name: 'react-router 6', kind: 'frontend' },
  { name: 'Go 1.23', kind: 'edge' },
  { name: 'fatedier/frp', kind: 'edge' },
  { name: 'paho.mqtt.golang', kind: 'edge' },
  { name: 'pppd + macvlan', kind: 'edge' },
];

const TYPING_FULL = 'Linux Link Control Panel · Linux 链路控制面板';

const Home: React.FC = () => {
  const navigate = useNavigate();
  const { token } = useAuthStore();
  const [typed, setTyped] = useState('');
  const [typingDone, setTypingDone] = useState(false);
  const [scrollY, setScrollY] = useState(0);

  // 打字机效果（完成后光标淡出）
  useEffect(() => {
    let i = 0;
    const timer = setInterval(() => {
      i += 1;
      setTyped(TYPING_FULL.slice(0, i));
      if (i >= TYPING_FULL.length) {
        clearInterval(timer);
        // 打字完成后稍作停留再隐藏光标
        setTimeout(() => setTypingDone(true), 600);
      }
    }, 55);
    return () => clearInterval(timer);
  }, []);

  // 视差滚动
  useEffect(() => {
    const onScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleEnter = () => {
    navigate(token ? '/' : '/login');
  };

  return (
    <div className="llcp-home">
      {/* 背景装饰 */}
      <div className="bg-grid" />
      <div className="bg-glow bg-glow-1" style={{ transform: `translateY(${scrollY * 0.2}px)` }} />
      <div className="bg-glow bg-glow-2" style={{ transform: `translateY(${scrollY * -0.15}px)` }} />
      <div className="bg-glow bg-glow-3" style={{ transform: `translateY(${scrollY * 0.1}px)` }} />
      <div className="bg-scanline" />

      {/* 顶部导航 */}
      <nav className="home-nav">
        <div className="nav-inner">
          <div className="nav-brand">
            <div className="brand-logo">
              <span className="logo-bracket">[</span>
              <span className="logo-text">LLCP</span>
              <span className="logo-bracket">]</span>
            </div>
            <div className="brand-meta">
              <span className="brand-name">Linux 链路控制面板</span>
              <span className="brand-version">v0.1.1</span>
            </div>
          </div>
          <div className="nav-actions">
            <a
              className="nav-link"
              href="https://github.com/myiis/llcp"
              target="_blank"
              rel="noreferrer"
            >
              <GithubOutlined /> 源码
            </a>
            <a className="nav-link" href="http://127.0.0.1:3000/api/docs" target="_blank" rel="noreferrer">
              <ApiOutlined /> API 文档
            </a>
            <button className="btn-primary" onClick={handleEnter}>
              {token ? '进入控制台' : '登录'} <ArrowRightOutlined />
            </button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="hero">
        <div className="hero-badge">
          <span className="pulse-dot" />
          <span>Edge-to-Cloud Device Orchestration</span>
        </div>
        <h1 className="hero-title">
          <span className="title-line title-line-1">Linux</span>
          <span className="title-line title-line-2">Link Control</span>
          <span className="title-line title-line-3">Panel</span>
        </h1>
        <div className="hero-typing">
          <span className="typing-prefix">$ </span>
          <span className="typing-text">{typed}</span>
          {!typingDone && <span className="typing-caret">|</span>}
        </div>
        <p className="hero-desc">
          面向边缘 Linux 设备的
          <span className="highlight"> 一体化链路控制平台</span>
          ：通过 MQTT 实时接入、FRP 反向隧道 SSH、PPPoE 多实例拨号、静态 IP 与策略路由编排，
          让分布式设备管理像调用本地 API 一样简单。
        </p>
        <div className="hero-cta">
          <button className="btn-primary btn-lg" onClick={handleEnter}>
            <RocketOutlined /> {token ? '进入控制台' : '立即登录'}
          </button>
          <button
            className="btn-ghost btn-lg"
            onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })}
          >
            <ThunderboltOutlined /> 了解功能
          </button>
        </div>
        <div className="hero-stats">
          <div className="stat">
            <div className="stat-value">6</div>
            <div className="stat-label">核心功能模块</div>
          </div>
          <div className="stat-divider" />
          <div className="stat">
            <div className="stat-value">3</div>
            <div className="stat-label">Git 仓库（主 + 2 插件）</div>
          </div>
          <div className="stat-divider" />
          <div className="stat">
            <div className="stat-value">MQTT</div>
            <div className="stat-label">长连接实时通道</div>
          </div>
          <div className="stat-divider" />
          <div className="stat">
            <div className="stat-value">FRP</div>
            <div className="stat-label">反向隧道穿透</div>
          </div>
        </div>
      </section>

      {/* 功能区 */}
      <section id="features" className="section features">
        <div className="section-head">
          <div className="section-tag">{'// CORE FEATURES'}</div>
          <h2 className="section-title">
            为<span className="grad-text">边缘设备</span>而生的控制平面
          </h2>
          <p className="section-desc">
            云端 REST API + MQTT 双向消息 + FRP 隧道 SSH，覆盖设备从上线、配置、监控到运维的完整生命周期。
          </p>
        </div>
        <div className="feature-grid">
          {FEATURES.map((f) => (
            <div key={f.title} className={`feature-card feature-${f.color}`}>
              <div className="feature-icon">{f.icon}</div>
              <div className="feature-tag">{f.tag}</div>
              <h3 className="feature-title">{f.title}</h3>
              <p className="feature-desc">{f.desc}</p>
              <div className="feature-glow" />
            </div>
          ))}
        </div>
      </section>

      {/* 架构图 */}
      <section className="section architecture">
        <div className="section-head">
          <div className="section-tag">{'// ARCHITECTURE'}</div>
          <h2 className="section-title">
            端到端<span className="grad-text">数据流</span>
          </h2>
          <p className="section-desc">三条链路，构成完整的云边协同闭环。</p>
        </div>
        <div className="arch-diagram">
          <div className="arch-layer">
            <div className="arch-node arch-node-fe">
              <div className="node-icon">⚛</div>
              <div className="node-title">React 前端</div>
              <div className="node-sub">llcp-frontend · Ant Design 5</div>
            </div>
          </div>
          <div className="arch-arrow arch-arrow-down">
            <span className="arrow-label">REST / WebSocket</span>
          </div>
          <div className="arch-layer">
            <div className="arch-node arch-node-be">
              <div className="node-icon">🚀</div>
              <div className="node-title">NestJS 后端</div>
              <div className="node-sub">llcp-backend · TypeORM · JWT</div>
            </div>
            <div className="arch-node arch-node-db">
              <div className="node-icon">🗄</div>
              <div className="node-title">MySQL</div>
              <div className="node-sub">server / net_info / ppp / static_ip</div>
            </div>
            <div className="arch-node arch-node-frps">
              <div className="node-icon">🌐</div>
              <div className="node-title">frps + HTTP API</div>
              <div className="node-sub">反向隧道注册中心</div>
            </div>
          </div>
          <div className="arch-arrow arch-arrow-down">
            <span className="arrow-label">MQTT Broker · tcp://125.65.79.28:1883</span>
          </div>
          <div className="arch-layer">
            <div className="arch-node arch-node-plugin1">
              <div className="node-icon">📡</div>
              <div className="node-title">mqtt_frpc</div>
              <div className="node-sub">上报 · FRP 客户端 · SSH 守护</div>
            </div>
            <div className="arch-node arch-node-plugin2">
              <div className="node-icon">🔌</div>
              <div className="node-title">ppp_go (llcp)</div>
              <div className="node-sub">PPPoE · 静态 IP · 策略路由</div>
            </div>
          </div>
          <div className="arch-arrow arch-arrow-down">
            <span className="arrow-label">pppd / macvlan / iproute2</span>
          </div>
          <div className="arch-layer">
            <div className="arch-node arch-node-device">
              <div className="node-icon">🖥</div>
              <div className="node-title">边缘 Linux 设备</div>
              <div className="node-sub">CentOS / Rocky / Armbian · x86 / ARM</div>
            </div>
          </div>
        </div>
      </section>

      {/* 技术栈 */}
      <section className="section stack">
        <div className="section-head">
          <div className="section-tag">{'// TECH STACK'}</div>
          <h2 className="section-title">
            经过验证的<span className="grad-text">技术组合</span>
          </h2>
        </div>
        <div className="stack-groups">
          <div className="stack-group">
            <div className="stack-group-title">☁ 云端后端</div>
            <div className="stack-pills">
              {STACK.filter((s) => s.kind === 'backend').map((s) => (
                <span key={s.name} className="pill pill-backend">{s.name}</span>
              ))}
            </div>
          </div>
          <div className="stack-group">
            <div className="stack-group-title">🎨 管理前端</div>
            <div className="stack-pills">
              {STACK.filter((s) => s.kind === 'frontend').map((s) => (
                <span key={s.name} className="pill pill-frontend">{s.name}</span>
              ))}
            </div>
          </div>
          <div className="stack-group">
            <div className="stack-group-title">📟 边缘插件</div>
            <div className="stack-pills">
              {STACK.filter((s) => s.kind === 'edge').map((s) => (
                <span key={s.name} className="pill pill-edge">{s.name}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 仓库 */}
      <section className="section repos">
        <div className="section-head">
          <div className="section-tag">{'// REPOSITORIES'}</div>
          <h2 className="section-title">
            三个仓库，<span className="grad-text">一个生态</span>
          </h2>
          <p className="section-desc">主仓库通过 Git Submodule 引用两个设备端插件仓库。</p>
        </div>
        <div className="repo-grid">
          <a className="repo-card repo-main" href="https://github.com/myiis/llcp" target="_blank" rel="noreferrer">
            <div className="repo-head">
              <GithubOutlined className="repo-icon" />
              <span className="repo-badge">主仓库</span>
            </div>
            <div className="repo-name">myiis/llcp</div>
            <div className="repo-desc">NestJS 后端 + React 前端 + Submodule 声明</div>
            <div className="repo-meta">
              <span>TypeScript</span><span>·</span><span>master</span>
            </div>
          </a>
          <a className="repo-card repo-plugin" href="https://github.com/myiis/mqtt_frpc" target="_blank" rel="noreferrer">
            <div className="repo-head">
              <GithubOutlined className="repo-icon" />
              <span className="repo-badge">Submodule</span>
            </div>
            <div className="repo-name">myiis/mqtt_frpc</div>
            <div className="repo-desc">设备侧 MQTT 上报 + FRP 反向隧道 + SSH 公钥守护</div>
            <div className="repo-meta">
              <span>Go 1.23</span><span>·</span><span>v2.3.4</span>
            </div>
          </a>
          <a className="repo-card repo-plugin" href="https://github.com/myiis/ppp_go" target="_blank" rel="noreferrer">
            <div className="repo-head">
              <GithubOutlined className="repo-icon" />
              <span className="repo-badge">Submodule</span>
            </div>
            <div className="repo-name">myiis/ppp_go</div>
            <div className="repo-desc">PPPoE 多实例拨号 + 静态 IP + 策略路由 + Web/TUI</div>
            <div className="repo-meta">
              <span>Go 1.21</span><span>·</span><span>llcp</span>
            </div>
          </a>
        </div>
      </section>

      {/* CTA */}
      <section className="section cta-section">
        <div className="cta-box">
          <div className="cta-terminal">
            <div className="term-bar">
              <span className="term-dot term-red" />
              <span className="term-dot term-yellow" />
              <span className="term-dot term-green" />
              <span className="term-title">llcp@edge:~</span>
            </div>
            <div className="term-body">
              <div><span className="term-prompt">$</span> git clone --recursive https://github.com/myiis/llcp.git</div>
              <div className="term-out">Cloning into 'llcp'... Submodule 'mqtt_frpc' registered. Submodule 'ppp_go' registered.</div>
              <div><span className="term-prompt">$</span> cd llcp/backend &amp;&amp; npm run start:dev</div>
              <div className="term-out">[Nest] LOG [NestFactory] Starting Nest application...</div>
              <div className="term-out term-success">✓ LLCP API listening on http://127.0.0.1:3000/api</div>
              <div><span className="term-prompt">$</span> <span className="term-cursor">▊</span></div>
            </div>
          </div>
          <div className="cta-content">
            <h2 className="cta-title">准备好接入你的第一台设备了吗？</h2>
            <p className="cta-desc">
              克隆仓库、启动服务、扫码上线，三分钟完成从边缘到云端的全链路搭建。
            </p>
            <div className="hero-cta">
              <button className="btn-primary btn-lg" onClick={handleEnter}>
                {token ? '进入控制台' : '登录控制台'} <ArrowRightOutlined />
              </button>
              <a
                className="btn-ghost btn-lg"
                href="https://github.com/myiis/llcp#5-边缘设备插件git-submodule"
                target="_blank"
                rel="noreferrer"
              >
                <GithubOutlined /> 部署文档
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="home-footer">
        <div className="footer-inner">
          <div className="footer-brand">
            <span className="footer-logo">[LLCP]</span>
            <span>Linux Link Control Panel · Linux 链路控制面板</span>
          </div>
          <div className="footer-links">
            <a href="https://github.com/myiis/llcp" target="_blank" rel="noreferrer">GitHub</a>
            <a href="http://127.0.0.1:3000/api/docs" target="_blank" rel="noreferrer">Swagger</a>
            <a href="/login">登录</a>
          </div>
          <div className="footer-copy">© 2026 LLCP · 由 NestJS + React + Go 驱动</div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
